import objectToURLSearchParams from '@lib/util/object-to-url-search-params';
import {
    buildApiResult,
    fetchWithTimeout,
    genericNetworkError,
    handleHttpErrors,
    FetchWithTimeoutOptions,
} from './api-util';
import { fetch_timeout_duration } from '@lib/constants/network-constants';
import { getRuntimeConfig } from '@lib/util/runtime-config-util';
import { getLogger } from '@lib/util/logging-util';

export default async function searchApi(
    method: string,
    path: string = '',
    payload?: {
        query?: Record<string, any>;
        body?: Record<string, any>;
    },
    root?: string
) {
    const requestId = `search-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const logger = getLogger();

    const runtimeConfig = getRuntimeConfig();

    logger.verbose(`[searchApi][${requestId}] ========== START ==========`);
    logger.verbose(`[searchApi][${requestId}] Timestamp: ${new Date().toISOString()}`);
    logger.verbose(`[searchApi][${requestId}] Method: ${method}`);
    logger.verbose(`[searchApi][${requestId}] Path (original): ${path}`);
    logger.verbose(`[searchApi][${requestId}] Root: ${root || '(none)'}`);
    logger.verbose(`[searchApi][${requestId}] Payload:`, JSON.stringify(payload, null, 2));

    if (path) {
        path = '/api' + path;
    }

    logger.verbose(`[searchApi][${requestId}] Path (after /api prefix): ${path}`);
    logger.verbose(`[searchApi][${requestId}] Runtime Config:`);
    logger.verbose(`[searchApi][${requestId}]   SEARCH_SERVICE_URL: ${runtimeConfig.SEARCH_SERVICE_URL ?? '(NOT SET)'}`);
    logger.verbose(`[searchApi][${requestId}]   CLIENT_API_KEY: ${runtimeConfig.CLIENT_API_KEY ? '***SET*** (length: ' + runtimeConfig.CLIENT_API_KEY.length + ')' : '(NOT SET)'}`);
    logger.verbose(`[searchApi][${requestId}]   SEARCH_SERVICE_TOKEN: ${runtimeConfig.SEARCH_SERVICE_TOKEN ? '***SET*** (length: ' + runtimeConfig.SEARCH_SERVICE_TOKEN.length + ')' : '(NOT SET)'}`);


    // Validate SEARCH_SERVICE_URL is an absolute URL
    if (!runtimeConfig.SEARCH_SERVICE_URL) {
        logger.error(`[searchApi][${requestId}] ========== CONFIGURATION ERROR ==========`);
        logger.error(`[searchApi][${requestId}] SEARCH_SERVICE_URL is not set!`);
        logger.error(`[searchApi][${requestId}] This will cause requests to be resolved relative to the current page.`);
        throw new Error('SEARCH_SERVICE_URL is not configured');
    }

    if (!runtimeConfig.SEARCH_SERVICE_URL.startsWith('http://') && !runtimeConfig.SEARCH_SERVICE_URL.startsWith('https://')) {
        logger.error(`[searchApi][${requestId}] ========== CONFIGURATION ERROR ==========`);
        logger.error(`[searchApi][${requestId}] SEARCH_SERVICE_URL is not an absolute URL: ${runtimeConfig.SEARCH_SERVICE_URL}`);
        logger.error(`[searchApi][${requestId}] It must start with http:// or https://`);
        logger.error(`[searchApi][${requestId}] Current value will be resolved relative to the current page!`);
    }

    const options: RequestInit = {
        method,
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': runtimeConfig.CLIENT_API_KEY,
        },
        cache: 'no-store',
    };

    if (payload?.body) {
        options.body = JSON.stringify(payload.body);
    }

    if (payload?.query) {
        const params = objectToURLSearchParams(payload.query).toString();
        path = `${path}?${params}`;
    }

    try {
        const fullUrl = `${runtimeConfig.SEARCH_SERVICE_URL}${root ?? ''}${path}`;

        logger.verbose(`[searchApi][${requestId}] ========== REQUEST ==========`);
        logger.verbose(`[searchApi][${requestId}] Full URL: ${fullUrl}`);
        logger.verbose(`[searchApi][${requestId}] Headers:`, JSON.stringify(options.headers, null, 2));
        if (options.body) {
            logger.verbose(`[searchApi][${requestId}] Body:`, options.body);
        }
        logger.verbose(`[searchApi][${requestId}] Timeout: ${fetch_timeout_duration}ms`);

        const fetchWithTimeoutOptions: FetchWithTimeoutOptions = {
            timeout: fetch_timeout_duration,
            enableInterceptor: true,
            interceptorServiceName: 'search-api',
            ...options,
        };

        // Generate CURL command for debugging (will be logged only on error)
        const curlCommand = generateCurlCommand(fullUrl, options);

        const startTime = Date.now();

        logger.verbose(`[searchApi][${requestId}] Sending request at ${new Date(startTime).toISOString()}...`);

        const response = await fetchWithTimeout(
            fullUrl,
            fetchWithTimeoutOptions
        );

        const endTime = Date.now();
        const duration = endTime - startTime;

        logger.verbose(`[searchApi][${requestId}] Response received in ${duration}ms`);
        logger.verbose(`[searchApi][${requestId}] ========== RESPONSE ==========`);
        logger.verbose(`[searchApi][${requestId}] Status: ${response?.status} ${response?.statusText}`);
        logger.verbose(`[searchApi][${requestId}] OK: ${response?.ok}`);
        logger.verbose(`[searchApi][${requestId}] Headers:`, JSON.stringify(Object.fromEntries(response?.headers || []), null, 2));

        if (!response?.ok) {
            logger.error(`[searchApi][${requestId}] ========== ERROR ==========`);
            logger.error(`[searchApi][${requestId}] ${response?.status} ${response?.statusText}: ${method} ${fullUrl}`);
            logger.verbose(`[searchApi][${requestId}] Duration: ${duration}ms`);
            logger.error(`[searchApi][${requestId}] CURL: ${curlCommand}`);
            logger.error(`[searchApi][${requestId}] ====================================`);
            logger.error(`[searchApi][${requestId}] Response headers:`, JSON.stringify(Object.fromEntries(response?.headers || []), null, 2));

            try {
                await handleHttpErrors(response);
            } catch (error) {
                logger.error(`[searchApi][${requestId}] handleHttpErrors caught:`, error);
            }
        }

        let body;
        try {
            // Check content-type before attempting to parse as JSON
            const contentType = response?.headers.get('content-type');

            logger.verbose(`[searchApi][${requestId}] Content-Type: ${contentType}`);

            if (contentType?.includes('application/json')) {
                const responseText = await response?.text();

                logger.verbose(`[searchApi][${requestId}] Response body length: ${responseText?.length || 0} characters`);

                try {
                    body = JSON.parse(responseText);
                    logger.verbose(`[searchApi][${requestId}] Parsed JSON successfully`);
                } catch (parseError) {
                    logger.error(`[searchApi][${requestId}] Failed to parse JSON:`, parseError);
                    logger.error(`[searchApi][${requestId}] Response length: ${responseText?.length || 0} characters`);
                    body = null;
                }
            } else {
                logger.error(`[searchApi][${requestId}] ========== UNEXPECTED CONTENT TYPE ==========`);
                logger.error(`[searchApi][${requestId}] URL: ${fullUrl}`);
                logger.error(`[searchApi][${requestId}] Status: ${response?.status} ${response?.statusText}`);
                logger.error(`[searchApi][${requestId}] Content-Type: ${contentType}`);
                logger.error(`[searchApi][${requestId}] Expected: application/json`);
                logger.error(`[searchApi][${requestId}] ========== CURL TO REPRODUCE ==========`);
                logger.error(curlCommand);
                logger.error(`[searchApi][${requestId}] ====================================`);

                const text = await response?.text();
                logger.error(`[searchApi][${requestId}] Response length: ${text?.length || 0} characters`);

                body = null;
            }
        } catch (error) {
            logger.error(`[searchApi][${requestId}] ========== PARSE ERROR ==========`);
            logger.error(`[searchApi][${requestId}] Error:`, error);
            logger.error(`[searchApi][${requestId}] URL: ${fullUrl}`);
            logger.error(`[searchApi][${requestId}] Status: ${response?.status} ${response?.statusText}`);
            logger.error(`[searchApi][${requestId}] ========== CURL TO REPRODUCE ==========`);
            logger.error(curlCommand);
            logger.error(`[searchApi][${requestId}] ====================================`);

            // Try to get the raw response text for debugging
            try {
                const text = await response?.text();
                logger.error(`[searchApi][${requestId}] Raw response length: ${text?.length || 0} characters`);
            } catch (textError) {
                logger.error(`[searchApi][${requestId}] Could not read response text:`, textError);
            }

            body = null;
        }

        if (body?.errors) {
            logger.error(`[searchApi][${requestId}] ========== ERRORS IN RESPONSE BODY ==========`);
            logger.error(`[searchApi][${requestId}] Errors:`, JSON.stringify(body.errors, null, 2));
            logger.error(`[searchApi][${requestId}] Full body:`, JSON.stringify(body, null, 2));
            throw body.errors[0];
        }

        logger.verbose(`[searchApi][${requestId}] ========== SUCCESS ==========`);
        logger.verbose(`[searchApi][${requestId}] Total duration: ${duration}ms`);
        if (body?.businesses) {
            logger.verbose(`[searchApi][${requestId}] Businesses count: ${body.businesses.length}`);
        }
        if (body?.total !== undefined) {
            logger.verbose(`[searchApi][${requestId}] Total results: ${body.total}`);
        }
        logger.verbose(`[searchApi][${requestId}] ========== END ==========`);

        return buildApiResult(response, body);
    } catch (error: any) {
        logger.error(`[searchApi][${requestId}] ========== EXCEPTION ==========`);
        logger.error(`[searchApi][${requestId}] Error type: ${error?.constructor?.name}`);
        logger.error(`[searchApi][${requestId}] Error message: ${error?.message}`);
        logger.error(`[searchApi][${requestId}] Error stack:`, error?.stack);
        logger.error(`[searchApi][${requestId}] Full error:`, JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
        logger.error(`[searchApi][${requestId}] ========== END ==========`);
        throw genericNetworkError;
    }
}

/**
 * Generates a CURL command equivalent to the fetch request for debugging purposes
 */
function generateCurlCommand(url: string, options: RequestInit): string {
    const method = options.method ?? 'GET';
    let curl = `curl -X ${method}`;

    // Add headers
    if (options.headers) {
        const headers = options.headers as Record<string, string>;
        Object.entries(headers).forEach(([key, value]) => {
            curl += ` \\\n  -H "${key}: ${value}"`;
        });
    }

    // Add body if present
    if (options.body) {
        const bodyStr = typeof options.body === 'string'
            ? options.body
            : JSON.stringify(options.body);
        // Escape single quotes in the body for shell
        const escapedBody = bodyStr.replace(/'/g, "'\\''");
        curl += ` \\\n  -d '${escapedBody}'`;
    }

    // Add URL (quoted to handle special characters)
    curl += ` \\\n  "${url}"`;

    return curl;
}
